/* eslint-disable no-restricted-imports */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { type LiteQuote } from '@frontend/api';
import { CheckmarkIcon } from '@frontend/components';

import { LEAD_TIME_UNITS } from 'constants/all';

import { formatPrice } from 'utils/formatters';

import { adjustments } from '../../data/actions';
import { getAdjustment } from '../../data/reducer';

const Wrapper = styled.div`
  background: #eee;
  border-bottom: 1px solid #ccc;
  border-top: 1px solid #ccc;
  padding-left: 10px;
  padding-right: 10px;
`;

const AdjustmentRow = styled.tr`
  td {
    background: #eee;
    padding-bottom: 7px;
    padding-top: 7px;
  }
`;

const TotalPriceCell = styled.td`
  padding-left: 5px;
`;

const AdjustmentTable = styled.table`
  text-align: right;
  width: 100%;
`;

const CheckBoxCell = styled.td`
  padding-left: 10px;
  ${(props) =>
    props.backgroundColor
      ? `background: ${props.backgroundColor};`
      : 'background: #fff !important;'}
`;

const CheckBoxWrapper = styled.div`
  background: ${(props) =>
    props.backgroundColor ? props.backgroundColor : '#fff'};
  display: flex;
  flex-direction: column;
  height: 21.82px;
  justify-content: center;
`;

const StyledCheckboxButton = styled.div`
  align-items: center;
  background-color: #ffffff;
  border-radius: 50%;
  border: solid 0.75px #4d79e9;
  display: flex;
  height: 12px;
  justify-content: center;
  width: 12px;

  :focus {
    border-radius: 50%;
    border: solid 0.75px #4d79e9;
    outline: none;
  }

  ${(props) =>
    props.square &&
    `
      border-radius: 0px;
    `}

  ${(props) =>
    props.checked &&
    `
      background-color: #4d79e9;
  `};
`;

const StyledInput = styled.input`
  border: 1px solid #ccc;
  border-radius: 2px;
  height: 25px;
  outline: none;
  text-align: center;
  ${(props) => `width: ${props.width};`}
`;

const formatInteger = (intString: string | null): number | '' => {
  if (intString !== null && intString !== '') {
    const strippedString = intString.replace(/[^0-9]/g, '');
    return strippedString !== '' ? parseInt(strippedString) : '';
  } else {
    return '';
  }
};

type AdjustmentProps = {
  quoteItemId: string;
  quote: LiteQuote;
  qtyWidth?: number;
  leadTimeWidth?: number;
  unitPriceWidth?: number;
  totalPriceWidth?: number;
  unitPricePrecision: number;
};

const Adjustment = ({
  quoteItemId,
  quote,
  qtyWidth,
  leadTimeWidth,
  unitPriceWidth,
  totalPriceWidth,
  unitPricePrecision,
}: AdjustmentProps) => {
  const dispatch = useDispatch();
  const deleteAdjustment = useCallback(
    (data: any) => {
      dispatch(adjustments.delete(data));
    },
    [dispatch],
  );
  const updateAdjustment = useCallback(
    (data: any) => {
      dispatch(adjustments.update(data));
    },
    [dispatch],
  );
  const adjustment = useSelector((state) => getAdjustment(state, quoteItemId));
  const quantityInputRef = useRef<HTMLInputElement>(null);
  const leadTimeDisplayUnit = quote?.leadTimeDisplayUnit;
  const [localQuantity, setLocalQuantity] = useState<'' | number>('');
  const [localLeadTime, setLocalLeadTime] = useState<'' | number>('');
  const [localUnitPrice, setLocalUnitPrice] = useState('');

  useEffect(() => {
    const { quantity, leadTime, unitPrice } = adjustment;
    setLocalQuantity(quantity);
    setLocalLeadTime(leadTime);
    setLocalUnitPrice(unitPrice || '');
  }, [adjustment]);

  //focus adjustmentQuantityInputRef when its value is first assigned
  useEffect(() => {
    if (quantityInputRef.current !== null) {
      quantityInputRef.current.focus();
    }
  }, []);

  return (
    <Wrapper>
      <AdjustmentTable>
        <AdjustmentRow>
          <CheckBoxCell backgroundColor="#eee">
            <CheckBoxWrapper backgroundColor="#eee">
              <StyledCheckboxButton checked={adjustment}>
                <CheckmarkIcon
                  color="#fff"
                  onClick={() => {
                    deleteAdjustment({
                      adjustmentId: adjustment.id,
                      quoteItemId: quoteItemId,
                    });
                  }}
                />
              </StyledCheckboxButton>
            </CheckBoxWrapper>
          </CheckBoxCell>
          <td width={`${qtyWidth}px`}>
            <StyledInput
              type="text"
              width="36px"
              value={localQuantity}
              ref={quantityInputRef}
              onChange={(e) => {
                setLocalQuantity(formatInteger(e.target.value));
              }}
              onBlur={() => {
                updateAdjustment({
                  quoteItemId: quoteItemId,
                  data: { quantity: localQuantity },
                });
              }}
            />
          </td>
          <td width={`${leadTimeWidth}px`}>
            <StyledInput
              type="text"
              width="35px"
              value={localLeadTime}
              onChange={(e) => {
                setLocalLeadTime(formatInteger(e.target.value));
              }}
              onBlur={() => {
                updateAdjustment({
                  quoteItemId: quoteItemId,
                  data: { leadTime: localLeadTime },
                });
              }}
            />
            &nbsp;{LEAD_TIME_UNITS[leadTimeDisplayUnit].unitName.toLowerCase()}
          </td>
          <td width={`${unitPriceWidth}px`}>
            <StyledInput
              type="text"
              width="76px"
              value={
                !isNaN(Number(localUnitPrice)) && localUnitPrice.trim() !== ''
                  ? `$${localUnitPrice}`
                  : ''
              }
              onChange={(e) => {
                if (
                  !e.target.value.includes('.') ||
                  e.target.value.split('.')[1].length <= unitPricePrecision
                ) {
                  setLocalUnitPrice(e.target.value.replace(/[^0-9.]/g, ''));
                }
              }}
              onBlur={() => {
                updateAdjustment({
                  quoteItemId: quoteItemId,
                  data: {
                    unitPrice:
                      parseFloat(localUnitPrice).toFixed(unitPricePrecision),
                  },
                });
                setLocalUnitPrice(
                  parseFloat(localUnitPrice).toFixed(unitPricePrecision),
                );
              }}
            />
          </td>
          <TotalPriceCell width={`${totalPriceWidth}px`}>{`${
            formatPrice(adjustment.totalPrice) || '$0.00'
          }`}</TotalPriceCell>
        </AdjustmentRow>
      </AdjustmentTable>
    </Wrapper>
  );
};
export default Adjustment;
